import { useMemo, Suspense } from "react";
import get from "get-value";

import cn from "@utils/cn";
import { evaluateConditional, interpolate, isValuePresent } from "@utils";
import { useKinndomContext } from "@contexts/KinndomContext";
import PageLoader from "@elements/PageLoader";

import FormBodyRow from "@components/form/FormBodyRow";
import FormBodySectionNote from "@components/form/FormBodySectionNote";
import FormBodySectionTitle from "@components/form/FormBodySectionTitle";
import FormField from "@components/form/FormField";

const FIELD_WIDTH_CLASS_NAMES = {
  1: "col-span-24 md:col-span-1",
  2: "col-span-24 md:col-span-2",
  3: "col-span-24 md:col-span-3",
  4: "col-span-24 md:col-span-4",
  5: "col-span-24 md:col-span-5",
  6: "col-span-24 md:col-span-6",
  7: "col-span-24 md:col-span-7",
  8: "col-span-24 md:col-span-8",
  9: "col-span-24 md:col-span-9",
  10: "col-span-24 md:col-span-10",
  11: "col-span-24 md:col-span-11",
  12: "col-span-24 md:col-span-12",
  13: "col-span-24 md:col-span-13",
  14: "col-span-24 md:col-span-14",
  15: "col-span-24 md:col-span-15",
  16: "col-span-24 md:col-span-16",
  17: "col-span-24 md:col-span-17",
  18: "col-span-24 md:col-span-18",
  19: "col-span-24 md:col-span-19",
  20: "col-span-24 md:col-span-20",
  21: "col-span-24 md:col-span-21",
  22: "col-span-24 md:col-span-22",
  23: "col-span-24 md:col-span-23",
  24: "col-span-24 md:col-span-24",
};

const defaultFieldConfig = {
  isVisible: true,
  isRequired: false,
  isDisabled: false,
  autoFocus: false,
};

const defaultProps = {
  body: undefined,
  handleFormFieldChange: () => {},
  formValues: {},
  initialFormValues: {},
  formErrors: {},
  formChanges: {},
  isAutofocusable: false,
  isDisabled: false,
};

export default function FormBody(props) {
  const {
    resource,
    path,
    body,
    handleFormFieldChange,
    formValues,
    initialFormValues,
    formErrors,
    formChanges,
    isAutofocusable,
    isDisabled,
  } = { ...defaultProps, ...props };

  const kinndom = useKinndomContext();

  const interpolationData = useMemo(() => ({
    kinndom: kinndom,
    config: kinndom.configuration,
    session: {
      currentUserId: window.appContext.currentUserId,
      currentUserIpAddress: window.appContext.currentUserIpAddress,
      currentUserAgent: window.appContext.currentUserAgent,
      isCurrentUserAdmin: window.appContext.isCurrentUserAdmin,
    },
    ...formValues,
  }), [kinndom, formValues]);

  const firstFocusableField = useMemo(() => {
    return body.flatMap((row) => row).find(({ config = {} }) => {
      if (config.isEnabled) return evaluateConditional(config.isEnabled, interpolationData);
      if (config.isDisabled) return !evaluateConditional(config.isDisabled, interpolationData);

      return true;
    });
  }, [body, interpolationData]);

  /**************************************************************************************************/

  if (!body) return null;

  return (
    <Suspense fallback={<PageLoader />}>
      {body.filter((row) => Array.isArray(row)).map((row, rowIndex) => {
        return (
          <FormBodyRow key={rowIndex}>
            {row.map((field, fieldIndex) => {
              if (!field) return null;

              const name = field.name;
              const type = field.type;

              const classNames = cn(FIELD_WIDTH_CLASS_NAMES[field.width]);

              if (type === "section_title") {
                return (
                  <FormBodySectionTitle
                    key={fieldIndex}
                    label={field.label}
                    hideBorder={field.hideBorder}
                  />
                );
              } else if (type === "section_note") {
                return (
                  <FormBodySectionNote
                    key={fieldIndex}
                    label={field.label}
                  />
                );
              } else if (type === "section_divider") {
                return (
                  <FormBodySectionTitle key={fieldIndex} />
                );
              } else if (type === "empty_space") {
                return (
                  <div className={classNames} key={fieldIndex} />
                );
              }

              /**************************************************************************************************/

              const interpolatedFieldConfig = interpolate(field.config, interpolationData);

              const config = {
                ...defaultFieldConfig,
                ...interpolatedFieldConfig,
              };

              if (isValuePresent(config.isRequired)) {
                config.isRequired = evaluateConditional(config.isRequired, interpolationData);
              }

              if (isValuePresent(config.isEnabled)) {
                config.isDisabled = !evaluateConditional(config.isEnabled, interpolationData);
              }

              if (isValuePresent(config.isDisabled)) {
                config.isDisabled = evaluateConditional(config.isDisabled, interpolationData);
              }

              if (isDisabled) {
                config.isDisabled = isDisabled;
              }

              if (isValuePresent(config.isHidden)) {
                config.isVisible = !evaluateConditional(config.isHidden, interpolationData);
              }

              if (isValuePresent(config.isVisible)) {
                config.isVisible = evaluateConditional(config.isVisible, interpolationData);
              }

              if (isAutofocusable && !isDisabled && firstFocusableField.name === name) {
                config.autoFocus = true;
              }

              /**************************************************************************************************/

              const getFromSource = (source) => get(source, name, { separator: "." });
              const value = getFromSource(formValues);
              const initialValue = getFromSource(initialFormValues);
              const error = getFromSource(formErrors);
              const change = getFromSource(formChanges);

              /**************************************************************************************************/

              if (!config.isVisible) return null;

              return (
                <div className={classNames} key={fieldIndex}>
                  <FormField
                    type={type}
                    name={name}
                    value={value}
                    initialValue={initialValue}
                    error={error}
                    change={change}
                    onChange={handleFormFieldChange}
                    config={config}
                    resource={resource}
                    path={path}
                  />
                </div>
              );
            })}
          </FormBodyRow>
        );
      })}
    </Suspense>
  );
}
